import React, { useState } from 'react';
import styles from './ToolCallCard.module.css';

interface ToolCallProps {
  name: string;
  parameters: Record<string, any>;
  result?: any;
}

/**
 * Simple bolt/lightning icon for tool calls
 */
const BoltIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className}
    width="12" 
    height="12" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

/**
 * Check icon for completed status
 */
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className}
    width="12" 
    height="12" 
    viewBox="0 0 24 24" 
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

/**
 * ToolCallCard Component
 * 
 * Compact, single-line display for function/tool calls.
 * Shows tool name with status, expandable for details.
 */
const ToolCallCard: React.FC<ToolCallProps> = React.memo(({ name, parameters, result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Format tool name: SNAKE_CASE -> readable
  const formatToolName = (str: string) => {
    // Check if there's a nested path.name
    const pathName = parameters['path']?.['name'];
    if (pathName) {
      return pathName;
    }
    // Otherwise use the name, converting SNAKE_CASE to readable
    return str.toLowerCase().replace(/_/g, ' ');
  };

  const toolName = formatToolName(name);
  const hasResult = result !== undefined;

  return (
    <div className={styles.card}>
      <div 
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <BoltIcon className={styles.icon} />
        <span className={styles.name}>{toolName}</span>
        {hasResult && <CheckIcon className={styles.check} />}
        <svg 
          className={styles.chevron}
          width="10" 
          height="10" 
          viewBox="0 0 24 24" 
          fill="none"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path 
            d="M6 9l6 6 6-6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isExpanded && (
        <div className={styles.body}>
          <div className={styles.section}>
            <div className={styles.label}>Parameters</div>
            <pre className={styles.code}>
              {JSON.stringify(parameters, null, 2)}
            </pre>
          </div>
          
          {hasResult && (
            <div className={styles.section}>
              <div className={styles.labelSuccess}>Result</div>
              <pre className={styles.code}>
                {typeof result === 'object' 
                  ? JSON.stringify(result, null, 2) 
                  : String(result)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.name === nextProps.name &&
    JSON.stringify(prevProps.parameters) === JSON.stringify(nextProps.parameters) &&
    JSON.stringify(prevProps.result) === JSON.stringify(nextProps.result)
  );
});

export default ToolCallCard;
